import { computed, ref, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  Home,
  BarChart3,
  Landmark,
  Wallet,
  Layers,
  Users,
  ReceiptText,
  Network,
  Settings,
  LogOut,
  KeyRound,
  UserCog,
} from 'lucide-vue-next'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { rails_url } from '@/modules/shared/utils/rails'
import { createOptions } from '@/modules/shared/utils/form'

export type NavItem = {
  key: string
  label: string
  icon: Component
  to: string | Record<string, unknown>
  hidden?: boolean
  badge?: string
}

export type ProfileLink = {
  label: string
  to: string | Record<string, unknown>
  icon: Component
}

function titleCase(str: string | null | undefined) {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.substring(1))
    .join(' ')
}

/**
 * Shared sidenav data — nav items, profile links, workspace selector — used by
 * `the-sidenav.vue` for both the desktop rail and the mobile drawer.
 */
export function useSidenav() {
  const route = useRoute()
  const authStore = useAuthStore()
  const workspaceStore = useWorkspaceStore()

  const taxYear = new Date().getFullYear() - 1

  const isAdmin = computed(() => authStore.current_user.role !== 'investor')
  const current_user = computed(() => authStore.current_user)

  // Investors only see the Companies list with an active / past-due subscription.
  const canViewCompanies = computed(() => ['active', 'past-due'].includes(authStore.current_user.status))

  /////////////////////////////////////////////////////////////////////////////
  // Workspace selector
  /////////////////////////////////////////////////////////////////////////////

  const selectedGroupKey = ref<string[]>([route.params.slug as string])
  const selectedGroup = computed(() =>
    authStore.current_user?.groups?.find((group: any) => group.subdomain === route.params.slug),
  )
  const workspaces = computed(() =>
    authStore.current_user?.groups?.reduce((acc: any, group: any) => {
      acc[group.subdomain] = { name: group.name }
      return acc
    }, {}),
  )
  const groupOptions = computed(() => createOptions(workspaces.value, { label: 'name' }))

  watch(selectedGroupKey, () => {
    const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
    window.location.href = `${baseUrl}/user?slug=${selectedGroupKey.value[0]}`
  })

  /////////////////////////////////////////////////////////////////////////////
  // Profile links
  /////////////////////////////////////////////////////////////////////////////

  const profile_links = computed<ProfileLink[]>(() => {
    if (isAdmin.value) {
      return [
        { label: 'Group Settings', to: { name: 'workspace.settings' }, icon: Settings },
        {
          label: 'Login Settings',
          to: {
            name: 'investing.individual.login-information',
            params: { individual_id: current_user.value.investor_id },
          },
          icon: KeyRound,
        },
        { label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut },
      ]
    }
    if (route.params.slug === 'connections') {
      return [{ label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut }]
    }
    return [
      {
        label: 'My Account',
        to: { name: 'investing.individual.account', params: { individual_id: current_user.value.investor_id } },
        icon: UserCog,
      },
      {
        label: 'Login Settings',
        to: {
          name: 'investing.individual.login-information',
          params: { individual_id: current_user.value.investor_id },
        },
        icon: KeyRound,
      },
      { label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut },
    ]
  })

  /////////////////////////////////////////////////////////////////////////////
  // Navigation items
  /////////////////////////////////////////////////////////////////////////////

  const navItems = computed<(NavItem | null)[]>(() => {
    if (isAdmin.value) {
      return [
        { key: 'dashboard', label: 'Dashboard', icon: Home, to: { name: 'dashboard' } },
        { key: 'portfolio', label: 'Portfolio', icon: BarChart3, to: { name: 'investing.portfolio' } },
        {
          key: 'companies',
          label: 'Companies',
          icon: Landmark,
          to: `${rails_url(route.params.slug as string)}/companies`,
        },
        null,
        {
          key: 'funds',
          label: 'Funds',
          icon: Wallet,
          to: { name: 'investing.entities', params: { entity_type: 'funds' } },
        },
        {
          key: 'spvs',
          label: 'SPVs',
          icon: Layers,
          to: { name: 'investing.entities', params: { entity_type: 'spvs' } },
        },
        { key: 'accounts', label: 'Accounts', icon: Users, to: { name: 'investing.individuals' } },
        null,
        {
          key: 'taxes',
          label: 'Taxes',
          icon: ReceiptText,
          to: {
            name: 'tax.entities',
            params: { slug: route.params.slug },
            query: { year: taxYear },
          },
          hidden: !workspaceStore.current.has_managed_entities,
        },
        {
          key: 'connections',
          label: 'Connections',
          icon: Network,
          to: { name: 'connecting.groups' },
          hidden: !workspaceStore.current.options.has_connections_access,
          badge: 'NEW',
        },
        { key: 'management', label: 'Management', icon: Settings, to: { name: 'workspace.settings' } },
      ]
    }

    if (route.params.slug === 'connections') {
      return [
        {
          key: 'connections',
          label: 'Connections',
          icon: Network,
          to: { name: 'connecting.individuals', params: { individualId: authStore.current_user.investor_id } },
        },
      ]
    }

    return [
      { key: 'home', label: 'Home', icon: Home, to: { name: 'dashboard' } },
      {
        key: 'accounts',
        label: 'Accounts',
        icon: Users,
        to: { name: 'investing.individuals' },
        hidden: workspaceStore.current.individual_count === 0,
      },
      {
        key: 'companies',
        label: 'Companies',
        icon: Landmark,
        to: `${rails_url(route.params.slug as string)}/companies`,
        hidden: !workspaceStore.current.options.is_company_list_enabled || !canViewCompanies.value,
      },
      {
        key: 'connections',
        label: 'Connections',
        icon: Network,
        to: { name: 'connecting.individuals', params: { individualId: authStore.current_user.investor_id } },
        hidden: !workspaceStore.current.options.has_connections_access,
      },
    ]
  })

  const isNavActive = (nav: NavItem) => {
    if (typeof nav.to === 'string') return false
    if (nav.to.name === 'investing.entities') return route.params.entity_type === (nav.to as any).params.entity_type
    return route.name === nav.to.name
  }

  return {
    titleCase,
    isAdmin,
    current_user,
    selectedGroupKey,
    selectedGroup,
    groupOptions,
    profile_links,
    navItems,
    isNavActive,
  }
}
